<template>
    <div ref="refRichTextMenu" class="rich-text-menu">
        <div class="menu-container">
            <!-- Menu Options -->
            <div v-if="editor" class="menu">
                <div
                    v-for="(option, index) in filteredOptions"
                    :key="index"
                    :class="getOptionClass(option)"
                >
                    <!-- AI Option -->
                    <template v-if="option === 'SellsyAI' && isSellsyAIEnabled">
                        <SellsyAIMenu
                            :editor="editor"
                            :option="option"
                            :disabled="disabled"
                            :typeRichText="typeRichText"
                            source="richtextmenu"
                        />
                        <hr class="divider" />
                    </template>
                    <!-- Link Option Custom -->
                    <AttachmentRichText
                        v-if="option === 'Attachment'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                        :attachmentEndpoint="attachmentEndpoint"
                        :hash="hash"
                    />
                    <!-- Heading Option -->
                    <HeadingRichText
                        v-if="option === 'Heading'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- Size Option -->
                    <SizeRichText
                        v-if="option === 'Size'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- TextAlign Option -->
                    <TextAlignRichText
                        v-if="option === 'TextAlign'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- Link Option Custom -->
                    <LinkRichText
                        v-if="option === 'Link'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- Image Option Custom -->
                    <ImageRichText
                        v-if="option === 'Image' || option === 'ImageResizable'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- Customtags Option Custom -->
                    <CustomTagsRichText
                        v-if="option === 'CustomTags'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- CustomTagsAndFields Option Custom -->
                    <CustomTagsAndFieldsRichText
                        v-if="option === 'CustomTagsAndFields'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                        :refRichText="refRichText"
                    />
                    <!-- Color Option Custom -->
                    <ColorRichText
                        v-if="option === 'Color'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- Highlight Option Custom -->
                    <HighlightRichText
                        v-if="option === 'Highlight'"
                        :editor="editor"
                        :option="option"
                        :disabled="disabled"
                    />
                    <!-- "More" / "Specific" Options -->
                    <CompassMenu
                        v-if="shouldShowOption(option)"
                        placement="top"
                        trigger="click"
                        customClass="custom-menu-container-more"
                        :disabled="disabled"
                    >
                        <CompassTooltip
                            :content="getExtensionOptionLabel(option[0])"
                            placement="top"
                            :delay="{ show: 0, hide: 1 }"
                            :disabled="disabled"
                        >
                            <CompassIconButton
                                :icon="getExtensionOptionIcon(option[0])"
                                size="small"
                                :disabled="disabled"
                                :active="activeStyle(option[0])"
                            />
                        </CompassTooltip>
                        <template #dropdown>
                            <CompassTooltip
                                v-for="(subOption, subIndex) in option.slice(1)"
                                :key="subOption + subIndex"
                                placement="right"
                                :delay="{ show: 0, hide: 1 }"
                                :disabled="disabled"
                            >
                                <template #content>
                                    <span v-if="getExtensionOption(subOption).label">
                                        {{ getExtensionOptionLabel(subOption) }}
                                    </span>
                                    <span
                                        v-if="getExtensionOption(subOption).shortcuts"
                                        class="option-shortcuts"
                                    >
                                        {{ getExtensionOptionShortcuts(subOption) }}
                                    </span>
                                </template>
                                <CompassIconButton
                                    v-if="option[0] === 'More'"
                                    :icon="getExtensionOptionIcon(subOption)"
                                    size="small"
                                    :disabled="disabled"
                                    @click="getExtensionOption(subOption).action()"
                                />
                                <CompassDropdownOption
                                    v-if="option[0] === 'Specific'"
                                    class="custom-menu-item"
                                    @itemSelected="getExtensionOption(subOption).action()"
                                >
                                    <CompassIcon
                                        :icon="getExtensionOptionIcon(subOption)"
                                        size="small"
                                    />
                                    <span class="typographies-paragraph-small-regular">
                                        {{ getExtensionOptionLabel(subOption) }}
                                    </span>
                                </CompassDropdownOption>
                            </CompassTooltip>
                        </template>
                    </CompassMenu>
                    <!-- One Option Default -->
                    <CompassTooltip
                        v-else-if="!allCustomOptions.includes(option) && option !== null"
                        placement="top"
                        :delay="{ show: 0, hide: 1 }"
                        :disabled="disabled"
                    >
                        <template #content>
                            <span>{{ getExtensionOptionLabel(option) }}</span>
                            <span class="option-shortcuts">
                                {{ getExtensionOptionShortcuts(option) }}
                            </span>
                        </template>
                        <CompassIconButton
                            :icon="getExtensionOptionIcon(option)"
                            size="small"
                            :disabled="disabled"
                            :active="activeStyle(option)"
                            @click="getExtensionOption(option).action()"
                        />
                    </CompassTooltip>
                    <!-- Divider -->
                    <template v-else-if="option === 'Divider'">
                        <hr class="divider" />
                    </template>
                </div>
                <!-- Slot custom option -->
                <div v-if="$slots['more-custom-options']">
                    <slot name="more-custom-options"></slot>
                </div>
            </div>
            <!-- Actions (buttons) -->
            <div v-if="$slots.actions" class="menu-action">
                <slot name="actions"></slot>
            </div>
        </div>
        <!-- Slot custom block -->
        <div v-if="$slots['more-block']" class="richtext-more-block">
            <slot name="more-block"></slot>
        </div>
    </div>
</template>

<script>
import Feature from 'Services/Feature';
import RichTextMixin from 'mixins/RichTextMixin';

import AttachmentRichText from './customoptions/AttachmentRichText.vue';
import ColorRichText from 'vcomponents/richtext/customoptions/ColorRichText.vue';
import HeadingRichText from './customoptions/HeadingRichText.vue';
import HighlightRichText from 'vcomponents/richtext/customoptions/HighlightRichText.vue';
import ImageRichText from './customoptions/ImageRichText.vue';
import LinkRichText from './customoptions/LinkRichText.vue';
import SizeRichText from './customoptions/SizeRichText.vue';
import TextAlignRichText from './customoptions/TextAlignRichText.vue';
import CustomTagsRichText from './customoptions/CustomTagsRichText.vue';
import CustomTagsAndFieldsRichText from './customoptions/CustomTagsAndFieldsRichText.vue';
import SellsyAIMenu from './customoptions/SellsyAIMenu.vue';

export default {
    name: 'RichTextMenu',
    components: {
        AttachmentRichText,
        ColorRichText,
        HeadingRichText,
        HighlightRichText,
        ImageRichText,
        LinkRichText,
        SizeRichText,
        TextAlignRichText,
        CustomTagsRichText,
        CustomTagsAndFieldsRichText,
        SellsyAIMenu,
    },
    mixins: [RichTextMixin],
    provide() {
        return { richTextMenu: this, parentHash: this.hash, refRichText: this.refRichText };
    },
    props: {
        editorMenu: {
            type: Object,
            required: true,
        },
        typeRichText: {
            type: String,
            default: '',
        },
        hash: {
            type: String,
            default: '',
        },
        attachmentEndpoint: {
            type: String,
            default: '',
        },
        refRichText: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            // Set here all options used custom components
            allCustomOptions: [
                'Divider',
                'Heading',
                'Size',
                'Color',
                'Highlight',
                'Table',
                'Link',
                'Image',
                'ImageResizable',
                'TextAlign',
                'CustomTags',
                'CustomTagsAndFields',
                'CustomTagsAI',
                'Mention',
                'SellsyAI',
                'Attachment',
            ],
        };
    },
    computed: {
        isSellsyAIEnabled() {
            return Feature.isEnabled('sellsy-ai');
        },
        filteredOptions() {
            return this.options.flat().filter((option) => {
                // Exclude Tiptap options (used for internal Tiptap features)
                return !option.endsWith('Tiptap');
            });
        },
    },
    mounted() {
        // Overwrite the editor data (mixin) with the prop from editor-content
        this.overwriteEditorWithProp(this.editorMenu);
    },
    methods: {
        // Check if the option (TIPTAP) is active
        activeStyle(option) {
            if (!option || !this.editor) return false;

            const activeStyles = {
                BulletList: 'bulletList',
                OrderedList: 'orderedList',
                CodeBlock: 'codeBlock',
                Size: 'textStyle',
            };

            const normalizedOption = activeStyles[option] || option.toLowerCase();

            return this.editor.isActive(normalizedOption);
        },
        shouldShowOption(option) {
            return (Array.isArray(option) && option[0] === 'More') || option[0] === 'Specific';
        },
        getOptionClass(option) {
            if (option === 'SellsyAI') {
                return 'menu__item--ai';
            }
            return 'menu__item';
        },
        getExtensionOptionLabel(option) {
            return this.$t.__(this.getExtensionOption(option).label);
        },
        getExtensionOptionIcon(option) {
            return this.getExtensionOption(option).icon;
        },
        getExtensionOptionShortcuts(option) {
            return this.getExtensionOption(option).shortcuts;
        },
    },
};
</script>

<style lang="scss" scoped>
.rich-text-menu {
    hr {
        width: 1px;
        height: 24px;
        margin: 0;
        border: none;
        border-radius: 1px;
        background: var(--colors-common-border-lighter);
    }
    .menu__item--ai {
        display: flex;
        gap: var(--spacing-0);
    }

    .menu-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-1);
    }
}
</style>

<style lang="scss">
.custom-menu-container,
.custom-menu-container-textalign,
.custom-menu-container-more,
.compass-popover__container {
    z-index: 1000 !important;
}

.custom-menu-container .compass-dropdown-option__label {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0;
    margin: 0;
}

.custom-menu-container .heading {
    margin: 0;
    line-height: normal;
}

.custom-menu-container-textalign .compass-menu__dropdown__content__options-container,
.custom-menu-container-more .compass-menu__dropdown__content__options-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-1);
}

.custom-menu-container-more .compass-tooltip {
    width: 100%;
}

.option-shortcuts {
    color: var(--colors-common-text-contrast-caption) !important;

    &-menu {
        color: var(--colors-common-text-standard-caption) !important;
    }
}

.compass-tooltip__item {
    z-index: 1001 !important;
}

.richtext-more-block {
    background: #f6f6f9;
}
</style>
